import React from 'react';
import PropTypes from 'prop-types';
import MediaThumbnail from './MediaThumbnail';
import {LanguageTabs} from '@/components/atoms';
import {ROUTES} from '@/utils/constants/routesConstants';
import {getFileNameFromUrl} from '@/helpers/shadcn/utils';
import useFreeKickGameReview from '@/hooks/useFreeKickGameReview';
import {GameReviewGridRow, GameSectionTitle} from './GameReviewComponents';
import {
  GAME_SPACING,
  createLeaderboardDownloadHandler
} from './gameReviewUtils';
import {
  GameReviewLayout,
  GameReviewSetupContent,
  GameReviewAttributesContent,
  EditableAccordionItem,
  GameImageWithFields
} from './GameReviewSharedSections';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';

const formatActivityType = (type) => type?.toUpperCase() || '-';

const FreeKickGameReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  isExcluded,
  getFormattedDate
}) => {
  const {downloadFileOnBlob} = useAzureBlob();
  const handleLeaderboardFileDownload =
    createLeaderboardDownloadHandler(downloadFileOnBlob);

  const {
    gameDisplayTabIndex,
    setGameDisplayTabIndex,
    gameSettingsTabIndex,
    setGameSettingsTabIndex,
    previewModal,
    handlePreview,
    handleClosePreview,
    asset,
    languages,
    languageCodes,
    gameDisplayLangCode,
    gameDisplayTranslation,
    gameSettingsTranslation
  } = useFreeKickGameReview(activityFormData);

  const isEmpty =
    !activityFormData || Object.keys(activityFormData).length === 0;

  return (
    <GameReviewLayout
      testId='freekick-game-review'
      containerClassName='freekick-game-review-container'
      innerClassName='freekick-game-review'
      loading={loading}
      showToast={showToast}
      toastMessage={toastMessage}
      handleToastClose={handleToastClose}
      handleViewActivity={handleViewActivity}
      previewModal={previewModal}
      handleClosePreview={handleClosePreview}
      isEmpty={isEmpty}>
      {/* Setup Accordion */}
      <EditableAccordionItem
        label='Setup'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_ADD)}>
        <GameReviewSetupContent
          testIdPrefix='freekick'
          activityFormData={activityFormData}
          getFormattedDate={getFormattedDate}
          isExcluded={isExcluded}
          formatActivityType={formatActivityType}
        />
      </EditableAccordionItem>

      {/* Display Accordion */}
      <EditableAccordionItem
        label='Display'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_DISPLAY)}>
        <div className='w-full'>
          <GameSectionTitle label='Game Display' className='mb-4' />

          {/* Language Tabs */}
          {languageCodes.length > 0 && (
            <div className='mb-4'>
              <LanguageTabs
                languages={languageCodes}
                fullLanguages={languages}
                activeTabIndex={gameDisplayTabIndex}
                setActiveTabIndex={setGameDisplayTabIndex}
                testIdPrefix='review-game-display-tab'
              />
            </div>
          )}

          {/* Game Image Section */}
          <GameImageWithFields
            title='Game Image'
            titleClassName='mb-3 text-sm font-semibold text-[#D0D0D0]'
            imageUrl={asset?.gameImage}
            imageAlt='game-image'
            handlePreview={handlePreview}
            previewTitle='Game Image Preview'
            thumbnailTestId='game-image-thumbnail-row'
            fieldsTestId={`game-display-fields-${gameDisplayLangCode}`}
            fields={[
              {
                label: 'Game Image',
                value: asset?.gameImage
                  ? getFileNameFromUrl(asset.gameImage)
                  : '-'
              },
              {label: 'Name', value: gameDisplayTranslation.title || '-'},
              {
                label: 'Tag Description',
                value: gameDisplayTranslation.tagDescription || '-'
              }
            ]}
          />

          {/* Logo Image Section */}
          <GameImageWithFields
            title='Logo Image'
            titleClassName='mb-3 text-sm font-semibold text-[#D0D0D0]'
            className='mb-6 mt-6'
            imageUrl={asset?.logoImage}
            imageAlt='logo-image'
            handlePreview={handlePreview}
            previewTitle='Logo Image Preview'
            thumbnailTestId='logo-image-thumbnail-row'
            fieldsTestId='logo-description-row'
            fields={[
              {
                label: 'Logo Image',
                value: asset?.logoImage
                  ? getFileNameFromUrl(asset.logoImage)
                  : '-'
              },
              {
                label: 'Logo Description',
                value: gameDisplayTranslation.gameDescription || '-',
                isHtml: true
              },
              {
                label: 'How to Play Rule',
                value: gameDisplayTranslation.howToPlayRule || '-',
                isHtml: true
              }
            ]}
          />

          {/* Game Settings Section */}
          <div className='mb-6 mt-6'>
            <GameSectionTitle
              label='Game Settings'
              className='mb-4'
              showDivider
            />

            {languageCodes.length > 0 && (
              <div className='mb-4'>
                <LanguageTabs
                  languages={languageCodes}
                  fullLanguages={languages}
                  activeTabIndex={gameSettingsTabIndex}
                  setActiveTabIndex={setGameSettingsTabIndex}
                  testIdPrefix='review-game-settings-tab'
                />
              </div>
            )}

            {/* First Team */}
            <GameSectionTitle label='First Team' className='mb-3 mt-4' />
            <div className='mb-4 flex items-start gap-6'>
              <MediaThumbnail
                url={asset?.firstTeamImage}
                alt='first-team-image'
                type='image'
                onPreview={() =>
                  handlePreview(
                    asset?.firstTeamImage,
                    'image',
                    'First Team Image Preview'
                  )
                }
              />
              <div className={`grid flex-1 ${GAME_SPACING.threeColGridClass}`}>
                <div>
                  <p className='text-sm text-[#D0D0D0]'>First Team Image</p>
                  <p className='font-bold text-white'>
                    {getFileNameFromUrl(asset?.firstTeamImage) || '-'}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-[#D0D0D0]'>First Team Name</p>
                  <p className='font-bold text-white'>
                    {gameSettingsTranslation.firstTeamName || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Second Team */}
            <GameSectionTitle label='Second Team' className='mb-3 mt-4' />
            <div className='mb-4 flex items-start gap-6'>
              <MediaThumbnail
                url={asset?.secondTeamImage}
                alt='second-team-image'
                type='image'
                onPreview={() =>
                  handlePreview(
                    asset?.secondTeamImage,
                    'image',
                    'Second Team Image Preview'
                  )
                }
              />
              <div className={`grid flex-1 ${GAME_SPACING.threeColGridClass}`}>
                <div>
                  <p className='text-sm text-[#D0D0D0]'>Second Team Image</p>
                  <p className='font-bold text-white'>
                    {getFileNameFromUrl(asset?.secondTeamImage) || '-'}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-[#D0D0D0]'>Second Team Name</p>
                  <p className='font-bold text-white'>
                    {gameSettingsTranslation.secondTeamName || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Tutorial */}
            <GameSectionTitle label='Tutorial' className='mb-3 mt-4' />
            <GameReviewGridRow
              dataTestId='freekick-tutorial-grid-row'
              items={[
                {
                  label: 'Description - Line 1',
                  value: gameSettingsTranslation.descriptionLine1 || '-'
                },
                {
                  label: 'Description - Line 2',
                  value: gameSettingsTranslation.descriptionLine2 || '-'
                }
              ]}
            />
          </div>

          {/* Background Images Section */}
          <div className='mb-6 mt-6'>
            <p className='mb-3 text-sm font-semibold text-[#D0D0D0]'>
              Background Images
            </p>

            <div className='grid grid-cols-3 gap-6'>
              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Background Menu</p>
                <MediaThumbnail
                  url={asset?.backgroundMenuImage}
                  alt='background-menu'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundMenuImage,
                      'image',
                      'Background Menu Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>
                  Background Select Team Image
                </p>
                <MediaThumbnail
                  url={asset?.backgroundSelectTeamImage}
                  alt='background-select-team'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundSelectTeamImage,
                      'image',
                      'Background Select Team Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Background Image</p>
                <MediaThumbnail
                  url={asset?.backgroundImage}
                  alt='background-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundImage,
                      'image',
                      'Background Image Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>
                  Background Next Level
                </p>
                <MediaThumbnail
                  url={asset?.backgroundNextLevelImage}
                  alt='background-next-level'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundNextLevelImage,
                      'image',
                      'Background Next Level Preview'
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Additional Images Section */}
          <div className='mb-6 mt-6'>
            <p className='mb-3 text-sm font-semibold text-[#D0D0D0]'>
              Additional Images
            </p>

            <div className='grid grid-cols-3 gap-6'>
              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Miss</p>
                <MediaThumbnail
                  url={asset?.missImage}
                  alt='miss-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.missImage,
                      'image',
                      'Miss Image Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Out</p>
                <MediaThumbnail
                  url={asset?.outImage}
                  alt='out-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(asset?.outImage, 'image', 'Out Image Preview')
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Game Over</p>
                <MediaThumbnail
                  url={asset?.gameOverImage}
                  alt='game-over-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.gameOverImage,
                      'image',
                      'Game Over Image Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Goal</p>
                <MediaThumbnail
                  url={asset?.goalImage}
                  alt='goal-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.goalImage,
                      'image',
                      'Goal Image Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>High Bar</p>
                <MediaThumbnail
                  url={asset?.highBarImage}
                  alt='high-bar-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.highBarImage,
                      'image',
                      'High Bar Image Preview'
                    )
                  }
                />
              </div>

              <div>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Right Bar</p>
                <MediaThumbnail
                  url={asset?.rightBarImage}
                  alt='right-bar-image'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.rightBarImage,
                      'image',
                      'Right Bar Image Preview'
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </EditableAccordionItem>

      {/* Attributes Accordion */}
      <EditableAccordionItem
        label='Attributes'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_ATTRIBUTES)}>
        <GameReviewAttributesContent
          activityFormData={activityFormData}
          handleLeaderboardFileDownload={handleLeaderboardFileDownload}
        />
      </EditableAccordionItem>
    </GameReviewLayout>
  );
};

FreeKickGameReview.propTypes = {
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  editButtonSchema: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(FreeKickGameReview);
